import React, { useEffect, useMemo, useState } from 'react'

import { AppProps } from '@/types/app-props'
import { PageContract } from '@/utils/register-page'

export type RenderFirstPageContractProps = {
  contracts: PageContract[]
  whenNoneMatch?: () => void
  appProps?: AppProps
}

/**
 * Renders the first page contract that matches the rendering criteria.
 *
 * @param contracts array of page contracts to evaluate, the order matters
 * @param whenNoneMatch callback to execute when none of the pages contracts match
 * @param appProps application properties to pass to the page component
 */
export function RenderFirstPageContract({
  contracts,
  whenNoneMatch,
  appProps,
}: RenderFirstPageContractProps) {
  /** Reflects the {@link ShouldPageRenderHook} result */
  const [shouldRender, setShouldRender] = useState<boolean | undefined>(false)

  const [page, setPage] = useState<JSX.Element | null>(null)

  /**
   * Generator function that yields page contracts one by one.
   * This allows sequential evaluation of each contract's rendering criteria.
   */
  const pagesContractSequence = useMemo(
    function* pagesContractGenerator() {
      for (const pageContract of contracts) yield pageContract
    },
    [contracts],
  )

  /**
   * Triggers when `shouldRender` changes.
   * If `shouldRender` is `false`, fetches the next page contract from the generator.
   * If no contracts remain, calls `whenNoneMatch`.
   * If a contract is found, resets `shouldRender` to `undefined` (pending result)
   * and sets `page` to the corresponding {@link PageComponent} to determine if it should render.
   */
  useEffect(() => {
    if (shouldRender === false) {
      const contract = pagesContractSequence.next().value
      setPage(null)

      if (!contract) return whenNoneMatch?.()

      setShouldRender(undefined)
      setPage(
        <contract.PageComponent
          isOptional
          ref={{
            current: { onShouldRenderChange: setShouldRender },
          }}
          appProps={appProps}
        />,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender])

  return page
}
